import { useMemo, useState } from "react";
import {
  List,
  TextField,
  DatagridConfigurable,
  DateField,
  NumberField,
  useTheme,
  FunctionField,
  RecordContextProvider,
} from "react-admin";
import getFirstAndLastDateOfCurrentMonth from "../../utils/firstLastDate";
import DownloadAction from "../../components/tableListActions/DownloadActionNew";
import PurchaseReturnCardExpandList from "../../components/purchaseReturnCards/expand/PurchaseReturnCardExpandList";
import purchaseReturnCardFilterList from "../../components/purchaseReturnCards/filter/purchaseReturnCardFilterList";
import PurchaseReturnCardAsideList from "../../components/purchaseReturnCards/aside/PurchaseReturnCardAsideList";
import useCompanyScopedListFilters from "../../hooks/useCompanyScopedListFilters";
import ManualReportList from "../../components/reports/ManualReportList";

const [firstDate, lastDate] = getFirstAndLastDateOfCurrentMonth();

const PurchaseReturnCardList = ({
  withActions = true,
  withFilters = true,
  grandTotalResource = "purchase-return-cards",
  title = "mine.list_title.purchase_return",
  parentFilter,
}) => {
  const [theme] = useTheme("light");
  const [appliedStandaloneFilter, setAppliedStandaloneFilter] = useState(null);
  const defaultFilterValues = useMemo(
    () => ({ start_date: firstDate, end_date: lastDate, approved: 1 }),
    []
  );
  const { permissions, currentCompanyFilter, filterDefaultValues } =
    useCompanyScopedListFilters(defaultFilterValues);

  const appliedFilter = parentFilter || appliedStandaloneFilter;

  const sharedFilter = useMemo(
    () => ({
      ...filterDefaultValues,
      ...(appliedFilter || {}),
      ...(currentCompanyFilter.company_id
        ? { company_id: currentCompanyFilter.company_id }
        : {}),
      start_date: appliedFilter?.start_date || firstDate,
      end_date: appliedFilter?.end_date || lastDate,
    }),
    [appliedFilter, currentCompanyFilter.company_id, filterDefaultValues]
  );

  const primaryFilter = useMemo(
    () =>
      withFilters
        ? { ...currentCompanyFilter, approved: 1, code: "RB-" }
        : { ...sharedFilter, approved: 1, code: "RB-" },
    [currentCompanyFilter, sharedFilter, withFilters]
  );

  const noDiscountFilter = useMemo(
    () => ({ ...sharedFilter, approved: 1, code: "RB-" }),
    [sharedFilter]
  );

  const discountFilter = useMemo(
    () => ({ ...sharedFilter, approved: 1, code: "RT-" }),
    [sharedFilter]
  );

  const grandTotalFilter = useMemo(
    () => ({ ...sharedFilter, code: undefined }),
    [sharedFilter]
  );

  const primaryFilterDefaultValues = withFilters
    ? filterDefaultValues
    : sharedFilter;

  const list = useMemo(() => {
    const PrimaryList = withFilters ? ManualReportList : List;
    return (
      <>
        <PrimaryList
          storeKey={false}
          resource="purchase-return-cards"
          title={title}
          empty={false}
          aside={
            <PurchaseReturnCardAsideList
              name="RETUR BELI TANPA DISKON"
              filter={noDiscountFilter}
              withGrandTotal={false}
            />
          }
          actions={withActions ? <DownloadAction /> : false}
          filters={
            withFilters ? purchaseReturnCardFilterList(permissions) : undefined
          }
          filter={primaryFilter}
          filterDefaultValues={primaryFilterDefaultValues}
          requiredFilters={["start_date", "end_date"]}
          onApplied={setAppliedStandaloneFilter}
          onReset={() => setAppliedStandaloneFilter(null)}
          queryOptions={{ meta: { sort: ["created_at DESC"] } }} // overide default sort
          sx={{
            flexDirection: "column",
          }}
        >
          <DatagridConfigurable
            bulkActionButtons={false}
            expand={<PurchaseReturnCardExpandList />}
            sx={{
              "& .RaDatagrid-expandedPanel > td": {
                p: 0,
                bgcolor: theme !== "dark" ? "#E7EBF0" : "grey.800",
              },
            }}
          >
            <TextField label="mine.label.depo" source="company.alias" />
            <TextField source="code" label="mine.label.receipt_no" />
            <DateField
              source="date"
              label="mine.label.date"
              options={{
                year: "numeric",
                month: "short",
                day: "2-digit",
              }}
            />
            <TextField label="mine.label.supplier" source="supplier.name" />
            <FunctionField
              textAlign="right"
              label="Box"
              render={(record) => {
                let rec = { total_return: 0 };
                for (let i = 0; i < record.purchase_return_items.length; i++) {
                  rec.total_return +=
                    +record.purchase_return_items[i].return_quantity;
                }
                return (
                  <RecordContextProvider value={rec}>
                    <NumberField source="total_return" />
                  </RecordContextProvider>
                );
              }}
            />
            <NumberField source="total" />
            <TextField source="note" label="mine.label.note" emptyText="-" />
          </DatagridConfigurable>
        </PrimaryList>
        <div style={{ margin: "22px 0 8px 0" }} /> {/* ================ */}
        {(!withFilters || appliedStandaloneFilter) ? <List
          key={`discount-${JSON.stringify(discountFilter)}`}
          storeKey={false}
          resource="purchase-return-cards"
          title=" "
          empty={false}
          aside={
            <PurchaseReturnCardAsideList
              name="RETUR BELI DENGAN DISKON 0.3%"
              filter={discountFilter}
              withGrandTotal={true}
              grandTotalResource={grandTotalResource}
              grandTotalFilter={grandTotalFilter}
            />
          }
          actions={false}
          filter={discountFilter}
          filterDefaultValues={sharedFilter}
          queryOptions={{
            meta: { sort: ["created_at DESC"] },
          }} // overide default sort
          sx={{
            flexDirection: "column",
          }}
        >
          <DatagridConfigurable
            bulkActionButtons={false}
            expand={<PurchaseReturnCardExpandList />}
            sx={{
              "& .RaDatagrid-expandedPanel > td": {
                p: 0,
                bgcolor: theme !== "dark" ? "#E7EBF0" : "grey.800",
              },
            }}
          >
            <TextField label="mine.label.depo" source="company.alias" />
            <TextField source="code" label="mine.label.receipt_no" />
            <DateField
              source="date"
              label="mine.label.date"
              options={{
                year: "numeric",
                month: "short",
                day: "2-digit",
              }}
            />
            <TextField label="mine.label.supplier" source="supplier.name" />
            <FunctionField
              textAlign="right"
              label="Box"
              render={(record) => {
                let rec = { total_return: 0 };
                for (let i = 0; i < record.purchase_return_items.length; i++) {
                  rec.total_return +=
                    +record.purchase_return_items[i].return_quantity;
                }
                return (
                  <RecordContextProvider value={rec}>
                    <NumberField source="total_return" />
                  </RecordContextProvider>
                );
              }}
            />
            <NumberField source="total" />
            <TextField source="note" label="mine.label.note" emptyText="-" />
          </DatagridConfigurable>
        </List> : null}
      </>
    );
  }, [
    theme,
    permissions,
    title,
    withActions,
    withFilters,
    grandTotalResource,
    noDiscountFilter,
    primaryFilter,
    primaryFilterDefaultValues,
    discountFilter,
    grandTotalFilter,
    sharedFilter,
    appliedStandaloneFilter,
  ]);
  return list;
};

export default PurchaseReturnCardList;
